// Oracle JSON operator lattice.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C2 (oracle row).
//
// Oracle has native `JSON` (21c+); pre-21c JSON-on-CLOB/BLOB with `IS JSON`
// constraint also surfaces in this lattice (per JCS-11 dot-notation v1
// inclusion). Includes function family + the Oracle-specific `IS [NOT] JSON`
// predicate + dot-notation path expression.

import type { Operator } from '../types';

const ORACLE_JSON: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: "= '${1:value}'" },
  { token: '<>', label: 'not equals', snippetTemplate: "<> '${1:value}'" },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  {
    token: 'IS JSON',
    label: 'is well-formed JSON',
    snippetTemplate: 'IS JSON',
    docsUrl: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/sqlrf/Conditions-IS-JSON.html',
  },
  {
    token: 'IS NOT JSON',
    label: 'is not well-formed JSON',
    snippetTemplate: 'IS NOT JSON',
    docsUrl: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/sqlrf/Conditions-IS-JSON.html',
  },
  {
    token: 'JSON_EXISTS',
    label: 'JSON path exists',
    snippetTemplate: "JSON_EXISTS(${1:col}, '${2:$.path}')",
    docsUrl: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/sqlrf/Conditions-JSON-EXISTS.html',
    requiresPath: true,
  },
  {
    token: 'JSON_VALUE',
    label: 'JSON path equals scalar',
    snippetTemplate: "JSON_VALUE(${1:col}, '${2:$.path}') = '${3:value}'",
    docsUrl: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/sqlrf/JSON_VALUE.html',
    requiresPath: true,
  },
  {
    token: 'JSON_QUERY',
    label: 'JSON path returns object/array',
    snippetTemplate: "JSON_QUERY(${1:col}, '${2:$.path}') IS NOT NULL",
    docsUrl: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/sqlrf/JSON_QUERY.html',
    requiresPath: true,
  },
  {
    token: '.',
    label: 'dot-notation path equals scalar',
    snippetTemplate: "${1:.path} = '${2:value}'",
    docsUrl: 'https://docs.oracle.com/en/database/oracle/oracle-database/19/adjsn/simple-dot-notation-access-json-data.html',
    requiresPath: true,
  },
]);

export function oracleJsonOperatorsFor(): readonly Operator[] {
  return ORACLE_JSON;
}
